import React from "react";
import type { ButtonProps } from "@material-ui/core/Button";
import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import { green, red } from "@material-ui/core/colors";
import type { Theme } from "@material-ui/core/styles";
import { createStyles, makeStyles } from "@material-ui/core/styles";
import clsx from "clsx";

import { Done as DoneIcon, PriorityHigh as PriorityHighIcon } from "@eroad/icons/material";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: "flex",
      alignItems: "center",
    },
    wrapper: {
      margin: theme.spacing(1),
      position: "relative",
    },
    buttonSuccess: {
      backgroundColor: green[500],
      "&:hover": {
        backgroundColor: green[700],
      },
    },
    buttonFailure: {
      backgroundColor: red[500],
      "&:hover": {
        backgroundColor: red[700],
      },
    },
    buttonProgress: {
      color: green[500],
      position: "absolute",
      top: "50%",
      left: "50%",
      marginTop: -12,
      marginLeft: -12,
    },
  }),
);

interface AsyncButtonProps extends ButtonProps {
  children: React.ReactNode;
  color?: "inherit" | "default" | "primary" | "secondary" | undefined;
  onClick: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => Promise<unknown>;
  timeout?: number;
}

const AsyncButton = ({ children, color, onClick, timeout, ...rest }: AsyncButtonProps): JSX.Element => {
  const classes = useStyles();
  const [loading, setLoading] = React.useState(false);
  const [success, setSuccess] = React.useState(false);
  const [error, setError] = React.useState(false);
  const timer = React.useRef<number>();

  const buttonClassname = clsx({
    [classes.buttonSuccess]: success,
    [classes.buttonFailure]: error,
  });
  React.useEffect(
    () => () => {
      clearTimeout(timer.current);
    },
    [],
  );
  const handleButtonClick = async (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    if (!loading) {
      setSuccess(false);
      setLoading(true);
      try {
        timer.current = window.setTimeout(() => {
          setSuccess(true);
          setLoading(false);
        }, timeout ?? 2000);
        await onClick(event);
        setSuccess(true);
      } catch (exception) {
        setSuccess(false);
        setLoading(false);
        setError(true);
      } finally {
        setLoading(false);
      }
    }
  };
  return (
    <div className={classes.root}>
      <div className={classes.wrapper}>
        <Button
          variant="contained"
          color={color || "primary"}
          className={buttonClassname}
          disabled={loading}
          startIcon={success && !error ? <DoneIcon /> : error && <PriorityHighIcon />}
          onClick={handleButtonClick}
          {...rest}
        >
          {children}
        </Button>
        {loading && <CircularProgress size={24} className={classes.buttonProgress} />}
      </div>
    </div>
  );
};

export default AsyncButton;
